import { FormField } from '@/components/form-section'
import type { OrganizationUser } from '@/lib/api/users/users-api'
import { isValidRecipientEmail, isValidRecipientPhone, type SalesFlowRecipient, type SalesFlowRecipientKind } from '@mediardex/contracts'
import { useMemo, useState } from 'react'
import { MdClose, MdSearch } from 'react-icons/md'

type RecipientChannel = 'phone' | 'email'

const KIND_LABELS: Record<RecipientChannel, Record<SalesFlowRecipientKind, string>> = {
    phone: { LEAD: 'O lead', ASSIGNED_USER: 'Responsável pelo lead', USERS: 'Usuários da equipe', CUSTOM: 'Números específicos' },
    email: { LEAD: 'O lead', ASSIGNED_USER: 'Responsável pelo lead', USERS: 'Usuários da equipe', CUSTOM: 'E-mails específicos' },
}

const KIND_HINTS: Record<RecipientChannel, Record<SalesFlowRecipientKind, string>> = {
    phone: {
        LEAD: 'Mensagem para o contato do lead.',
        ASSIGNED_USER: 'Aviso para quem atende o lead, no telefone do cadastro dele. Lead sem responsável não recebe.',
        USERS: 'Aviso para cada usuário marcado, no telefone do cadastro.',
        CUSTOM: 'Aviso para os números informados (ex.: o WhatsApp do gestor).',
    },
    email: {
        LEAD: 'E-mail para o endereço do lead.',
        ASSIGNED_USER: 'Aviso para quem atende o lead, no e-mail do cadastro dele. Lead sem responsável não recebe.',
        USERS: 'Um e-mail para cada usuário marcado.',
        CUSTOM: 'Um e-mail para cada endereço informado.',
    },
}

export function isTeamRecipient(recipient?: SalesFlowRecipient | null): boolean {
    return !!recipient && recipient.kind !== 'LEAD'
}

/** Quem recebe um passo de envio. Sem `recipient` no config, é o lead. */
export default function RecipientConfig({
    idPrefix,
    channel,
    value,
    users,
    onChange,
}: {
    idPrefix: string
    channel: RecipientChannel
    value?: SalesFlowRecipient | null
    users: OrganizationUser[]
    onChange: (recipient: SalesFlowRecipient) => void
}) {
    const kind = value?.kind ?? 'LEAD'

    return (
        <>
            <FormField label="Quem recebe" htmlFor={`${idPrefix}-kind`} hint={KIND_HINTS[channel][kind]}>
                <select
                    id={`${idPrefix}-kind`}
                    className="select select-bordered select-sm w-full"
                    value={kind}
                    onChange={e => onChange({ kind: e.target.value as SalesFlowRecipientKind, userIds: value?.userIds ?? [], contacts: value?.contacts ?? [] })}
                >
                    {(Object.keys(KIND_LABELS[channel]) as SalesFlowRecipientKind[]).map(option => (
                        <option key={option} value={option}>
                            {KIND_LABELS[channel][option]}
                        </option>
                    ))}
                </select>
            </FormField>

            {kind === 'USERS' && (
                <FormField label="Usuários" required>
                    <UserMultiSelect
                        users={users}
                        selected={value?.userIds ?? []}
                        detail={channel}
                        onChange={userIds => onChange({ kind, userIds })}
                    />
                </FormField>
            )}

            {kind === 'CUSTOM' && (
                <FormField
                    label={channel === 'phone' ? 'Números' : 'E-mails'}
                    htmlFor={`${idPrefix}-contact`}
                    required
                    hint={channel === 'phone' ? 'Com DDD. Número de outro país: comece com + e o código do país.' : undefined}
                >
                    <ContactListInput
                        id={`${idPrefix}-contact`}
                        channel={channel}
                        contacts={value?.contacts ?? []}
                        onChange={contacts => onChange({ kind, contacts })}
                    />
                </FormField>
            )}
        </>
    )
}

/**
 * Marca vários usuários da organização, com busca. `detail` mostra o telefone ou
 * o e-mail de cada um e avisa quem não tem o dado no cadastro.
 */
export function UserMultiSelect({
    users,
    selected,
    detail,
    onChange,
}: {
    users: OrganizationUser[]
    selected: number[]
    detail?: RecipientChannel
    onChange: (userIds: number[]) => void
}) {
    const [search, setSearch] = useState('')
    const selectedSet = useMemo(() => new Set(selected.map(Number)), [selected])
    const term = search.trim().toLowerCase()
    // E-mail só entra quando o termo tem "@": todos da organização dividem o domínio, e buscar
    // parte do nome da empresa traria a lista inteira.
    const visible = term
        ? users.filter(u => u.name.toLowerCase().includes(term) || (term.includes('@') && (u.email ?? '').toLowerCase().includes(term)))
        : users

    const toggle = (userId: number) => {
        onChange(selectedSet.has(userId) ? selected.filter(id => Number(id) !== userId) : [...selected, userId])
    }

    const withoutData = detail ? users.filter(u => selectedSet.has(u.id) && !(detail === 'phone' ? u.phone : u.email)) : []

    return (
        <div className="space-y-2">
            <label className="input input-bordered input-sm flex w-full items-center gap-2">
                <MdSearch className="text-base-content/40" />
                <input type="text" className="grow" placeholder="Buscar usuário" value={search} onChange={e => setSearch(e.target.value)} />
            </label>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-base-300">
                {visible.length === 0 ? (
                    <p className="p-3 text-sm text-base-content/50">Nenhum usuário encontrado.</p>
                ) : (
                    visible.map(user => {
                        const contact = detail === 'phone' ? user.phone : detail === 'email' ? user.email : null
                        return (
                            <label key={user.id} className="flex cursor-pointer items-center gap-3 border-b border-base-200 px-3 py-2 last:border-b-0 hover:bg-base-200/50">
                                <input type="checkbox" className="checkbox checkbox-sm checkbox-primary" checked={selectedSet.has(user.id)} onChange={() => toggle(user.id)} />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm">{user.name}</span>
                                    {detail && (
                                        <span className={`block truncate text-xs ${contact ? 'text-base-content/50' : 'text-warning'}`}>
                                            {contact || (detail === 'phone' ? 'Sem telefone no cadastro' : 'Sem e-mail no cadastro')}
                                        </span>
                                    )}
                                </span>
                            </label>
                        )
                    })
                )}
            </div>
            <p className="text-xs text-base-content/50">
                {selected.length === 0 ? 'Nenhum usuário marcado.' : `${selected.length} ${selected.length === 1 ? 'usuário marcado' : 'usuários marcados'}.`}
            </p>
            {withoutData.length > 0 && (
                <p className="text-xs leading-relaxed text-warning">
                    {withoutData.map(u => u.name).join(', ')} {withoutData.length === 1 ? 'não vai' : 'não vão'} receber: falta{' '}
                    {detail === 'phone' ? 'o telefone' : 'o e-mail'} no cadastro do usuário.
                </p>
            )}
        </div>
    )
}

function ContactListInput({
    id,
    channel,
    contacts,
    onChange,
}: {
    id: string
    channel: RecipientChannel
    contacts: string[]
    onChange: (contacts: string[]) => void
}) {
    const [draft, setDraft] = useState('')
    const isValid = channel === 'phone' ? isValidRecipientPhone : isValidRecipientEmail
    const draftValue = draft.trim()
    const draftInvalid = draftValue !== '' && !isValid(draftValue)

    const add = () => {
        if (!draftValue || draftInvalid) return
        const key = (value: string) => (channel === 'phone' ? value.replace(/\D/g, '') : value.toLowerCase())
        if (!contacts.some(c => key(c) === key(draftValue))) onChange([...contacts, draftValue])
        setDraft('')
    }

    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <input
                    id={id}
                    type={channel === 'phone' ? 'tel' : 'email'}
                    className={`input input-bordered input-sm flex-1 ${draftInvalid ? 'input-error' : ''}`}
                    placeholder={channel === 'phone' ? '(77) 99999-0000' : 'nome@empresa.com.br'}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            add()
                        }
                    }}
                />
                <button type="button" className="btn btn-sm btn-outline" disabled={!draftValue || draftInvalid} onClick={add}>
                    Adicionar
                </button>
            </div>
            {draftInvalid && <p className="text-xs text-error">{channel === 'phone' ? 'Número incompleto.' : 'E-mail inválido.'}</p>}
            {contacts.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                    {contacts.map(contact => (
                        <span key={contact} className={`badge gap-1 py-3 ${isValid(contact) ? 'badge-ghost' : 'badge-error'}`}>
                            {contact}
                            <button
                                type="button"
                                className="text-base-content/50 hover:text-error"
                                aria-label={`Remover ${contact}`}
                                onClick={() => onChange(contacts.filter(c => c !== contact))}
                            >
                                <MdClose size={14} />
                            </button>
                        </span>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-base-content/50">Nenhum {channel === 'phone' ? 'número' : 'e-mail'} adicionado.</p>
            )}
        </div>
    )
}
